'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, AlertCircle, ArrowRight, Loader2, ShieldCheck, CheckCircle2, Lock } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { Toaster, toast } from 'sonner';
import { useForm } from 'react-hook-form';

interface LoginFormData {
  email: string;
  password: string;
}

// Credentials are NOT shown in UI — kept only for auth logic
const ADMIN_EMAIL = 'iambajaj@outlook.com';
const ADMIN_PASSWORD = 'Alliancetrade@123';

export default function LoginScreen() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormData>();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1000));

    const email = data.email.trim().toLowerCase();
    const password = data.password;

    if (email === ADMIN_EMAIL.toLowerCase() && password === ADMIN_PASSWORD) {
      toast.success('Welcome back, Rahul Bajaj');
      setTimeout(() => router.push('/admin-panel'), 600);
    } else {
      // Check admin-created customer credentials from localStorage
      let customerFound = false;
      try {
        const raw = localStorage.getItem('alliance_customer_credentials');
        if (raw) {
          const creds: { portalEmail: string; portalPassword: string; company: string; name: string; customerId: string }[] = JSON.parse(raw);
          const match = creds.find(
            (c) => c.portalEmail.trim().toLowerCase() === email && c.portalPassword === password
          );
          if (match) {
            customerFound = true;
            // Store active customer session
            localStorage.setItem('alliance_active_customer', JSON.stringify({ customerId: match.customerId, company: match.company, name: match.name }));
            toast.success(`Welcome back, ${match.name}`);
            setTimeout(() => router.push('/customer-dashboard'), 600);
          }
        }
      } catch {
        // ignore storage errors
      }
      if (!customerFound) {
        setError('root', { message: 'Invalid email or password. Please contact your Alliance Bank Relationship Manager.' });
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--background)' }}>
      <Toaster position="bottom-right" richColors />

      {/* Left panel — brand */}
      <div
        className="hidden lg:flex lg:w-[45%] xl:w-[42%] flex-col justify-between p-10 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #061840 0%, #0A2463 50%, #0D2F7A 100%)' }}
      >
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, #D4A017 0%, transparent 50%), radial-gradient(circle at 80% 20%, #4A90D9 0%, transparent 40%)`,
          }}
        />
        <div className="absolute bottom-0 right-0 w-64 h-64 opacity-5 rounded-full" style={{ background: 'var(--accent)', transform: 'translate(30%, 30%)' }} />

        {/* Logo */}
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-8">
            <AppLogo size={44} />
            <div>
              <p className="text-white font-800 text-xl leading-tight">Alliance Trade Portal</p>
              <p className="text-sm font-500" style={{ color: 'var(--accent)' }}>LC Bill Discounting Platform</p>
            </div>
          </div>

          <div className="space-y-6 mt-12">
            <h2 className="text-white text-3xl font-700 leading-snug">
              Secure trade finance,<br />
              <span style={{ color: 'var(--accent)' }}>end to end.</span>
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
              Submit BOE requests, track bank workflow in real-time, and manage your entire LC discounting lifecycle from a single secure portal.
            </p>
          </div>

          <div className="mt-10 space-y-3">
            {[
              'Multi-stage BOE request tracking',
              'Secure document vault with bank verification',
              'Real-time chat with trade desk',
              'Discounting terms and maturity schedule',
            ].map((feature) => (
              <div key={`feature-${feature}`} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(212,160,23,0.2)' }}>
                  <CheckCircle2 size={12} aria-hidden="true" style={{ color: 'var(--accent)' }} />
                </div>
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom badges */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
            <ShieldCheck size={14} aria-hidden="true" style={{ color: 'var(--accent)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>256-bit SSL Encrypted</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
            <Lock size={14} aria-hidden="true" style={{ color: 'var(--accent)' }} />
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>RBI Compliant</span>
          </div>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <AppLogo size={36} />
            <div>
              <p className="font-800 text-lg" style={{ color: 'var(--primary)' }}>Alliance Trade Portal</p>
              <p className="text-xs text-muted-foreground">LC Bill Discounting Platform</p>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-2xl font-700 text-foreground">Sign in to your account</h1>
            <p className="text-sm text-muted-foreground mt-1.5">
              Login credentials are shared by your Alliance Bank Account Manager.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Root error */}
            {errors.root && (
              <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg border" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
                <AlertCircle size={15} aria-hidden="true" className="flex-shrink-0 mt-0.5" style={{ color: '#DC2626' }} />
                <p className="text-sm" style={{ color: '#991B1B' }}>{errors.root.message}</p>
              </div>
            )}

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-600 text-foreground" htmlFor="email">Email Address</label>
              <div className="relative">
                <input
                  id="email"
                  type="email"
                  placeholder="your@company.com"
                  className="input-field"
                  {...register('email', {
                    required: 'Email address is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                  })}
                />
              </div>
              {errors.email && <p className="text-xs" style={{ color: '#DC2626' }}>{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-sm font-600 text-foreground" htmlFor="password">Password</label>
                <button type="button" className="text-xs font-500 hover:underline" style={{ color: 'var(--primary)' }}>
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className="input-field pr-10"
                  {...register('password', {
                    required: 'Password is required',
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
                </button>
              </div>
              {errors.password && <p className="text-xs" style={{ color: '#DC2626' }}>{errors.password.message}</p>}
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2">
              <input id="remember" type="checkbox" className="w-4 h-4 rounded accent-primary" />
              <label htmlFor="remember" className="text-sm text-muted-foreground">Keep me signed in for 7 days</label>
            </div>

            {/* Submit */}
            <button type="submit" disabled={isLoading} className="btn-primary w-full justify-center py-3 text-sm">
              {isLoading ? (
                <>
                  <Loader2 size={15} aria-hidden="true" className="animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign in to Portal</span>
                  <ArrowRight size={15} aria-hidden="true" />
                </>
              )}
            </button>
          </form>

          {/* Account Manager note */}
          <div className="mt-4 flex items-start gap-2 px-3 py-2.5 rounded-lg" style={{ backgroundColor: 'rgba(10,36,99,0.05)', border: '1px solid rgba(10,36,99,0.1)' }}>
            <ShieldCheck size={14} aria-hidden="true" className="flex-shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
            <p className="text-xs text-muted-foreground leading-relaxed">
              <span className="font-600 text-foreground">No self-registration.</span> Account access is provisioned by your Alliance Bank Relationship Manager. Contact your RM to request access.
            </p>
          </div>

          <p className="text-center text-[11px] text-muted-foreground mt-6">
            © 2026 Alliance Trade Portal · Confidential · All rights reserved
          </p>
        </div>
      </div>
    </div>
  );
}